/**
 * Centered dialog for multi-step flows and long forms. Body scrolls inside the panel
 * (max height uses dynamic viewport units for mobile browsers).
 *
 * For compact action menus and mobile-first pickers, prefer BottomSheet — it becomes a
 * bottom sheet on narrow viewports and a centered panel on desktop.
 */
import React from 'react';
import { Dialog as HeadlessDialog, Transition } from '@headlessui/react';
import { clsx } from 'clsx';
import { X } from 'lucide-react';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
  showCloseButton?: boolean;
}

const sizeStyles = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
  '2xl': 'sm:max-w-6xl',
};

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  className,
  showCloseButton = false,
}: ModalProps) {
  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <HeadlessDialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-6">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <HeadlessDialog.Panel
                className={clsx(
                  'flex max-h-[min(90dvh,calc(100dvh-2rem))] w-full transform flex-col overflow-hidden rounded-2xl text-left align-middle shadow-xl transition-all',
                  'bg-white dark:bg-dark-800',
                  sizeStyles[size],
                  className
                )}
              >
                {(title || showCloseButton) && (
                  <div className="shrink-0 border-b border-gray-200 px-4 pb-3 pt-5 dark:border-dark-700 sm:px-6 sm:pb-4 sm:pt-6">
                    <div className="flex items-start justify-between gap-4">
                      {title ? (
                        <HeadlessDialog.Title as="h3" className="type-dialog-title pr-2">
                          {title}
                        </HeadlessDialog.Title>
                      ) : (
                        <HeadlessDialog.Title as="h2" className="sr-only">
                          Dialog
                        </HeadlessDialog.Title>
                      )}
                      {showCloseButton && (
                        <button
                          type="button"
                          onClick={onClose}
                          aria-label="Close modal"
                          className="shrink-0 rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-dark-700 dark:hover:text-gray-200"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div
                  className={clsx(
                    'min-h-0 flex-1 overflow-y-auto px-4 sm:px-6',
                    title || showCloseButton ? 'pb-6 pt-4' : 'py-6'
                  )}
                >
                  {children}
                </div>

                {footer && (
                  <div className="shrink-0 border-t border-gray-200 dark:border-dark-700">
                    <div className="safe-bottom flex flex-wrap items-center justify-end gap-3 px-4 pb-8 pt-4 sm:px-6 sm:pb-5">
                      {footer}
                    </div>
                  </div>
                )}
              </HeadlessDialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </HeadlessDialog>
    </Transition>
  );
}
